<!-- client/src/components/Chart.vue -->
<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
    <div class="date-range-controls">
      <label>
        Start:
        <input type="date" v-model="startDate" @change="renderChart" />
      </label>
      <label>
        End:
        <input type="date" v-model="endDate" @change="renderChart" />
      </label>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register required components
ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

export default {
  name: 'Chart',
  props: {
    logs: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      chartInstance: null,
      startDate: '',
      endDate: '',
    };
  },
  methods: {
    renderChart() {
      const canvas = this.$refs.chartCanvas;
      if (!canvas) return;

      // Wait for next tick to ensure canvas is properly sized
      this.$nextTick(() => {
        // Clean up existing chart
        if (this.chartInstance) {
          this.chartInstance.destroy();
          this.chartInstance = null;
        }

        // Process the data
        const processedLogs = this.logs.map(log => 
          log._custom ? log._custom.value : log
        );

        // Filter logs by date range if set
        let filteredLogs = processedLogs;
        if (this.startDate) {
          const start = new Date(this.startDate);
          filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= start);
        }
        if (this.endDate) {
          // Include the whole end day (UTC)
          const end = new Date(this.endDate);
          end.setDate(end.getDate() + 1); // Move to next day
          end.setUTCHours(0, 0, 0, 0); // Set to start of next day
          end.setMilliseconds(end.getMilliseconds() - 1); // Adjust to end of the previous day
          filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= end);
        }

        // Sort logs by timestamp ascending (oldest to newest)
        filteredLogs = filteredLogs.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        const labels = filteredLogs.map(log => 
          new Date(log.timestamp).toLocaleString()
        );
        
        // Prepare all numerical datasets
        const datasetConfigs = [
          { key: 'temperature', label: 'Temperature (°F)', borderColor: 'red' },
          { key: 'ph', label: 'pH', borderColor: 'blue' },
          { key: 'salinity', label: 'Salinity', borderColor: 'green', hidden: true },
          { key: 'ammonia', label: 'Ammonia (ppm)', borderColor: '#ff9800', hidden: true },
          { key: 'nitrite', label: 'Nitrite (ppm)', borderColor: '#9c27b0', hidden: true },
          { key: 'nitrate', label: 'Nitrate (ppm)', borderColor: '#3f51b5' },
          { key: 'phosphase', label: 'Phosphate (ppm)', borderColor: '#009688' },
          { key: 'calcium', label: 'Calcium (ppm/10)', borderColor: '#e91e63' },
          { key: 'alkalinity', label: 'Alkalinity (dKH)', borderColor: '#607d8b' },
          { key: 'magnesium', label: 'Magnesium (ppm/10)', borderColor: '#8bc34a', hidden: true },
        ];

        const datasets = datasetConfigs.map(cfg => ({
          label: cfg.label,
          data: filteredLogs.map(log => {
            if (cfg.key === 'calcium' && log[cfg.key] != null) {
              return log[cfg.key] / 10;
            }
            if (cfg.key === 'magnesium' && log[cfg.key] != null) {
              return log[cfg.key] / 10;
            }
            return log[cfg.key];
          }),
          borderColor: cfg.borderColor,
          fill: false,
          tension: 0.1,
          hidden: cfg.hidden || false,
          spanGaps: true,
        }));

        this.chartInstance = new ChartJS(canvas, {
          type: 'line',
          data: {
            labels,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 0 // Disable animation for better performance
            },
            scales: {
              x: {
                type: 'category',
                title: {
                  display: true,
                  text: 'Timestamp',
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Values',
                },
              },
            },
          },
        });
      });
    },
  },
  watch: {
    logs: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    startDate() {
      this.renderChart();
    },
    endDate() {
      this.renderChart();
    },
  },
  mounted() {
    // Optionally set default date range to cover all logs
    if (this.logs.length) {
      const timestamps = this.logs.map(log => {
        const l = log._custom ? log._custom.value : log;
        return new Date(l.timestamp);
      });
      const minDate = new Date(Math.min(...timestamps));
      const maxDate = new Date(Math.max(...timestamps));
      this.startDate = minDate.toISOString().slice(0, 10);
      this.endDate = maxDate.toISOString().slice(0, 10);
    }
    this.renderChart();
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy(); // Properly clean up the chart instance
      this.chartInstance = null;
    }
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
  position: relative;
  background: rgb(var(--v-theme-background));
  margin-bottom:10px;
}

.date-range-controls {
  position: absolute;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 0px;
  margin-top: -20px;
  z-index: 2;
}

.date-range-controls input[type="date"] {
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
}
</style>